import { Injectable, inject, type TemplateRef, type ViewContainerRef } from '@angular/core';
import type { ComponentType } from '@angular/cdk/portal';
import { BrnDialogService, type BrnDialogOptions, type BrnDialogRef } from '@spartan-ng/brain/dialog';
import { bindBackClose } from './dialog-back-close';

export type DialogContent = ComponentType<unknown> | TemplateRef<unknown>;

export interface HlmDialogOptions extends Partial<BrnDialogOptions> {
  /** Opt-in — a dialog that swallows Back is wrong where the page behind it is the real destination. */
  backCloses?: boolean;
}

/**
 * The one opening path for product dialogs. Contract: ../CONTRACT.md (read it before adding
 * a call site). The Brn surface stays reachable — `vcr` and the sanctioned options pass through
 * untouched; only `hasBackdrop: true` is defaulted, since every product dialog wants it.
 */
@Injectable({ providedIn: 'root' })
export class HlmDialogOpener {
  private readonly dialog = inject(BrnDialogService);

  /**
   * Definite flow: the dialog appears on the click itself, in the same tick. The context stays a
   * SYNCHRONOUS object (Brn spreads it); data still loading travels as promised/signal FIELDS
   * inside it, resolving behind the dialog's own skeleton — never as an awaited pre-open fetch.
   */
  open<TContext extends object, TResult = unknown>(
    content: DialogContent,
    vcr?: ViewContainerRef,
    context?: TContext,
    options?: HlmDialogOptions,
  ): BrnDialogRef<TResult> {
    const { backCloses, ...brnOptions } = options ?? {};
    const ref = this.dialog.open<TContext, TResult>(content, vcr, context, { hasBackdrop: true, ...brnOptions });
    if (backCloses) bindBackClose(ref);
    return ref;
  }

  /**
   * Conditional flow, checking-first: opens INSTANTLY in a checking state; when `check` settles,
   * `route` drives the already-open ref (advance a step, close, hand over to another dialog).
   * Prefer this when the dialog usually appears. A rejected `check` falls back to closing the
   * dialog unless `onError` routes it explicitly.
   */
  openWithCheck<TContext extends object, TVerdict, TResult = unknown>(
    content: DialogContent,
    vcr: ViewContainerRef | undefined,
    context: TContext,
    check: Promise<TVerdict>,
    route: (verdict: TVerdict, ref: BrnDialogRef<TResult>) => void,
    options?: HlmDialogOptions,
    onError?: (error: unknown, ref: BrnDialogRef<TResult>) => void,
  ): BrnDialogRef<TResult> {
    const ref = this.open<TContext, TResult>(content, vcr, context, options);
    check.then(
      verdict => route(verdict, ref),
      error => (onError ? onError(error, ref) : ref.close(undefined)),
    );
    return ref;
  }

  /**
   * Conditional flow, rare-outcome: the await stays BEFORE the open. Using this method marks the
   * call site as a deliberate rare-conditional (the dialog usually does NOT appear) — never a
   * shortcut for an unmigrated definite flow. `null` from `resolveContext` means no dialog.
   */
  async openAfterCheck<TContext extends object, TResult = unknown>(
    content: DialogContent,
    vcr: ViewContainerRef | undefined,
    resolveContext: () => Promise<TContext | null>,
    options?: HlmDialogOptions,
  ): Promise<BrnDialogRef<TResult> | undefined> {
    const context = await resolveContext();
    if (context === null) return undefined;
    return this.open<TContext, TResult>(content, vcr, context, options);
  }
}
